/**
 * Setup › API keys: the keys this platform holds for the clinic software.
 *
 * Paste a key, and it is used on the next pull or delivery -- no restart. A
 * key is never shown back: only where it comes from, its last four
 * characters, who saved it, and what the clinic said about it. The read key
 * is checked with the clinic before it is saved, so one the clinic refuses
 * cannot quietly stop the hourly pull.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'
import { Card, ErrorNote, Loading, Note } from '../components/ui'

interface ApiKey {
  name: string
  label: string
  purpose: string
  prefix: string
  source: string
  last4: string | null
  set_by_name: string | null
  set_at: string | null
  checked_at: string | null
  check_ok: boolean | null
  check_note: string | null
  webhook_url: string | null
}

const when = (iso: string) => iso.slice(0, 16).replace('T', ' ')

const SOURCE_TEXT: Record<string, string> = {
  'saved here': 'Saved here',
  'server file': 'From the server file',
  'not set': 'Not set',
  unreadable: 'Saved here, but it cannot be read on this server — paste it again',
}

function KeyCard({ k }: { k: ApiKey }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState('')
  const [shown, setShown] = useState(false)
  const done = () => {
    setValue('')
    setShown(false)
    void queryClient.invalidateQueries({ queryKey: ['api-keys'] })
  }
  const save = useMutation({
    mutationFn: () => api.put<ApiKey>(`/settings/api-keys/${k.name}`, { value }),
    onSuccess: done,
  })
  const check = useMutation({
    mutationFn: () => api.post<ApiKey>(`/settings/api-keys/${k.name}/check`),
    onSuccess: (fresh) =>
      queryClient.setQueryData<ApiKey[]>(['api-keys'], (old) =>
        (old ?? []).map((row) => (row.name === fresh.name ? fresh : row)),
      ),
  })
  const remove = useMutation({
    mutationFn: () => api.del<ApiKey>(`/settings/api-keys/${k.name}`),
    onSuccess: done,
  })

  const status =
    (SOURCE_TEXT[k.source] ?? k.source) +
    (k.last4 ? ` · ending …${k.last4}` : '') +
    (k.set_by_name && k.set_at ? ` · by ${k.set_by_name}, ${when(k.set_at)}` : '')

  return (
    <Card title={k.label} tight>
      <ErrorNote error={save.error ?? check.error ?? remove.error} />
      <div className="muted" style={{ marginBottom: 8 }}>
        {k.purpose}
      </div>
      <div style={{ marginBottom: 8 }}>
        <span
          className={`pill ${k.source === 'not set' || k.source === 'unreadable' ? 'returned' : 'approved'}`}
        >
          {k.source === 'not set' ? 'missing' : k.source === 'unreadable' ? 'unreadable' : 'in use'}
        </span>{' '}
        {status}
      </div>
      {k.check_note && (
        <div className="faint" style={{ marginBottom: 8 }}>
          {k.check_ok === false ? 'The clinic said: ' : ''}
          {k.check_note}
          {k.checked_at && <> · checked {when(k.checked_at)}</>}
        </div>
      )}
      {k.webhook_url && (
        <div style={{ marginBottom: 10 }}>
          <div className="faint">The address the clinic sends its billing to:</div>
          <div className="mono" style={{ wordBreak: 'break-all' }}>
            {k.webhook_url}
          </div>
        </div>
      )}
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          if (value.trim()) save.mutate()
        }}
      >
        <input
          type={shown ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          placeholder={`Paste the new key (${k.prefix}…)`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <div className="shrink">
          <button type="button" onClick={() => setShown(!shown)} disabled={!value}>
            {shown ? 'Hide' : 'Show'}
          </button>
        </div>
        <div className="shrink">
          <button className="primary" type="submit" disabled={save.isPending || !value.trim()}>
            {save.isPending ? 'Checking and saving…' : 'Save'}
          </button>
        </div>
      </form>
      <div className="row" style={{ marginTop: 8 }}>
        {k.prefix === 'jcl_' && k.source !== 'not set' && (
          <div className="shrink">
            <button disabled={check.isPending} onClick={() => check.mutate()}>
              {check.isPending ? 'Asking the clinic…' : 'Check with the clinic'}
            </button>
          </div>
        )}
        {(k.source === 'saved here' || k.source === 'unreadable') && (
          <div className="shrink">
            <button
              disabled={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Remove the key saved here? The server file’s key, if there is one, is used again.',
                  )
                )
                  remove.mutate()
              }}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </Card>
  )
}

export default function ApiKeys() {
  const keys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<ApiKey[]>('/settings/api-keys'),
  })
  if (keys.isLoading) return <Loading what="Loading the API keys" />
  return (
    <>
      <ErrorNote error={keys.error} />
      <Note>
        Keys pasted here are stored encrypted and are never shown back — only their last four
        characters. A new key is used on the next pull or delivery; nothing needs restarting. A key
        saved here takes the place of the one in the server file, and removing it puts the server
        file’s back.
      </Note>
      {(keys.data ?? []).map((k) => (
        <KeyCard key={k.name} k={k} />
      ))}
    </>
  )
}
